import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { COLORS } from '../../../utils/constants';
import { 
  Target, 
  FileEdit, 
  Sparkles,
  Lightbulb
} from 'lucide-react';

const ExperienceLevel = ({ data, onNext, onBack }) => {
  const [experienceLevel, setExperienceLevel] = useState(data.experienceLevel || '');
  const [error, setError] = useState('');

  const experienceLevels = [
    {
      value: 'experienced',
      label: "Yes, I'm experienced",
      icon: Target,
      description: 'I have built forms with various platforms before'
    },
    {
      value: 'some-experience',
      label: "I've used Typeform / Google Forms",
      icon: FileEdit,
      description: 'Familiar with basic form builders'
    },
    {
      value: 'beginner',
      label: "I'm new to form builders",
      icon: Sparkles,
      description: 'This is my first time using a form builder'
    }
  ];

  const handleSubmit = () => {
    if (!experienceLevel) {
      setError('Please select your experience level');
      return;
    }

    onNext({ experienceLevel });
  };

  return (
    <div style={{ maxWidth: '650px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 0px)' }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ marginBottom: 'clamp(24px, 4vw, 32px)', textAlign: 'center' }}
      >
        <h2
          style={{
            fontSize: 'clamp(24px, 5vw, 32px)',
            fontWeight: '700',
            color: COLORS.black,
            marginBottom: '8px'
          }}
        >
          What's your experience level?
        </h2>
        <p style={{ fontSize: 'clamp(14px, 3vw, 16px)', color: COLORS.gray, lineHeight: '1.5' }}>
          This helps us provide the right guidance and tips as you build
        </p>
      </motion.div>

      {/* Experience Options */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 2vw, 16px)', marginBottom: 'clamp(24px, 4vw, 32px)' }}
      >
        {experienceLevels.map((level, index) => {
          const isSelected = experienceLevel === level.value;
          const IconComponent = level.icon;
          return (
            <motion.button
              key={level.value}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setExperienceLevel(level.value);
                setError('');
              }}
              style={{
                padding: 'clamp(16px, 3vw, 20px) clamp(20px, 3vw, 24px)',
                backgroundColor: isSelected ? `${COLORS.blue}10` : COLORS.white,
                border: `2px solid ${
                  isSelected ? COLORS.blue : error ? '#EF4444' : COLORS.lightGray
                }`,
                borderRadius: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'clamp(12px, 2vw, 16px)',
                transition: 'all 0.3s ease',
                fontFamily: 'inherit',
                textAlign: 'left',
                position: 'relative',
                boxShadow: isSelected ? '0 8px 24px rgba(59, 130, 246, 0.15)' : 'none'
              }}
            >
              {/* Icon */}
              <div
                style={{
                  flexShrink: 0,
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconComponent
                  size={Math.min(32, window.innerWidth * 0.06)}
                  color={isSelected ? COLORS.blue : COLORS.darkGray}
                />
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: '600',
                    color: isSelected ? COLORS.blue : COLORS.darkGray,
                    marginBottom: '6px'
                  }}
                >
                  {level.label}
                </div>
                <div
                  style={{
                    fontSize: 'clamp(13px, 2.5vw, 14px)',
                    color: COLORS.gray,
                    lineHeight: '1.5'
                  }}
                >
                  {level.description}
                </div>
              </div>

              {/* Check indicator */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: COLORS.blue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3 8L6 11L13 4"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </motion.div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ color: '#EF4444', fontSize: '13px', marginTop: '-16px', marginBottom: '16px' }}
        >
          {error}
        </motion.p>
      )}

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        style={{
          padding: 'clamp(14px, 2vw, 16px)',
          backgroundColor: `${COLORS.blue}08`,
          borderRadius: '12px',
          border: `1px solid ${COLORS.blue}30`,
          marginBottom: '32px'
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Lightbulb size={20} color={COLORS.blue} />
          <p
            style={{
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              color: COLORS.darkGray,
              lineHeight: '1.6',
              margin: 0
            }}
          >
            Don't worry! No matter your experience level, Fylo is designed to be intuitive and
            easy to use. We'll guide you every step of the way.
          </p>
        </div>
      </motion.div>

      {/* Navigation Buttons */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        style={{
          display: 'flex',
          gap: 'clamp(10px, 2vw, 12px)',
          marginTop: 'clamp(32px, 5vw, 40px)',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <button
          onClick={onBack}
          style={{
            padding: 'clamp(12px, 2vw, 14px) clamp(20px, 3vw, 28px)',
            backgroundColor: COLORS.white,
            color: COLORS.darkGray,
            border: `2px solid ${COLORS.lightGray}`,
            borderRadius: 'clamp(10px, 2vw, 12px)',
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = COLORS.darkGray;
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = COLORS.lightGray;
            e.target.style.transform = 'translateY(0)';
          }}
        >
          ← Back
        </button>

        <button
          onClick={handleSubmit}
          style={{
            padding: 'clamp(12px, 2vw, 14px) clamp(24px, 4vw, 32px)',
            backgroundColor: COLORS.blue,
            color: COLORS.white,
            border: 'none',
            borderRadius: 'clamp(10px, 2vw, 12px)',
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          Continue →
        </button>
      </motion.div>
    </div>
  );
};

export default ExperienceLevel;